import { stratify } from "d3-hierarchy";
import isDev from "./utils";
import { notificationControl } from "./controls/notification-control";
import { LANG, Resp } from "./models/common_model";
import { method } from "lodash";

export const host = isDev() ? 'http://localhost:5000' : ''
const absoluteHost = isDev() ? 'http://localhost:5000' : `${window.location.protocol}//${window.location.host}`

export const hostUrl = (path: string) => `${host}/api/${path}`

export const hostAbsoluteUrl = (path: string) => `${absoluteHost}/api/${path}`

export function httpRequest<D>(path: string, body: any, method: 'post' | 'get' | 'delete' | 'put', headers: HeadersInit, abortController?: AbortController): Promise<D> {
    try {
        const result = fetch(hostUrl(path), { body, method, headers, credentials: "include", signal: abortController?.signal}).then(res => {
            return res.json().catch((reason => {
                console.error('promise catch request error', reason);
                notificationControl.showError('http error, system is busy');
            })).then(json => {
                const resp = json as Resp<D>;
                switch(resp.status) {
                    case 0:
                        return resp.data;
                    case 1:
                        //error
                        notificationControl.showError('system error: ' + resp.data);
                        return Promise.reject(resp);
                    case 2:
                        //invalid
                        return Promise.reject(resp);
                    case 3:
                        //need login
                        if (!window.location.pathname.startsWith('/login')) {
                            localStorage.setItem('last_route', window.location.pathname)
                        }
                        window.location.href='/login'

                        break;
                    case 4:
                        //no permission
                        notificationControl.showError('you don\'t have permission to do this');
                        console.error('you don\'t have permission to ' + resp.data)
                        return Promise.reject(resp);
                    case 5:
                        //need change password
                        if (!window.location.pathname.startsWith('/force_change_password')) {
                            localStorage.setItem('last_route', window.location.pathname)
                        }
                        window.location.href='/force_change_password'

                        break;
                    default:
                        notificationControl.showError('unknow error code: ' + resp.status);
                        return Promise.reject(resp);
                }

                return resp.data;
            });
        }, (e) => {
            if(e.name === "AbortError") {
                console.info('http request is abort');
                return;
            }
            console.error('request error', e);
            notificationControl.showError('http error, system is busy');
            return Promise.reject({status: 1});
        });

        return result as any;
    } catch (e) {
        notificationControl.showError('system is very busy');

        return Promise.reject(e);
    }
}


const REST_REQUEST_CACHE: {[cacheKey: string]: any} = {}
const REST_REQUEST_CACHE_QUEUE: {[cacheKey: string]: any} = {}
export function restRequest<D>(path: string, body: any = null, method: 'post' | 'get' | 'delete' | 'put' = 'get', abortController?: AbortController, cachedKey: string = ''): Promise<D> {
    if (body) {
        body = JSON.stringify(body)
    }

    if (cachedKey) {
        const cache = REST_REQUEST_CACHE[path + '-' + cachedKey];
        if (cache) {
            return Promise.resolve(JSON.parse(JSON.stringify(cache)));
        }
        
        if (REST_REQUEST_CACHE_QUEUE[path + '-' + cachedKey]) {
            return REST_REQUEST_CACHE_QUEUE[path + '-' + cachedKey].promise;
        } else {
            let resolve, reject;
            const promise = new Promise((rs, rj) => {
                resolve = rs;
                reject = rj;
            })
            REST_REQUEST_CACHE_QUEUE[path + '-' + cachedKey] = {promise, resolve, reject};
        }
    }


    const result = httpRequest<D>(path, body, method, { 'Content-Type': 'application/json' }, abortController);

    if (cachedKey) {
        return result.then((resp) => {
            REST_REQUEST_CACHE[path + '-' + cachedKey] = JSON.parse(JSON.stringify(resp));
            if (REST_REQUEST_CACHE_QUEUE[path + '-' + cachedKey]) {
                REST_REQUEST_CACHE_QUEUE[path + '-' + cachedKey].resolve(JSON.parse(JSON.stringify(resp)));
                delete REST_REQUEST_CACHE_QUEUE[path + '-' + cachedKey];
            }
            return resp;
        }, err => {
            if (REST_REQUEST_CACHE_QUEUE[path + '-' + cachedKey]) {
                REST_REQUEST_CACHE_QUEUE[path + '-' + cachedKey].reject(err);
                delete REST_REQUEST_CACHE_QUEUE[path + '-' + cachedKey];
            }

            return err;
        })
    } else {
        return result;
    }
}

export function requestMeasurementNameAndType(testingId: string) {
    return restRequest<{ name: string, key: string, export_file_name: string, data_type: number }[]>('testings/measurement_name_and_type/' + testingId, null, 'get', undefined, `measurement_name_and_type_${testingId}`);
}

export function waitForLongTimeTask<D>(key: string): Promise<D> {
    const request: () => Promise<D> = () => {
        return restRequest<{status: number, data: D}>('commons/wait_for_longtime_task', {key}, 'post').then(result => {
            if (result.status === 1) {
                return result.data;
            }

            return new Promise<D>((resolve, reject) => {
                setTimeout(() => resolve(request()), 3000);
            });
        })
    } 

    return request();
}


export function formatEnumName(typeId: number, name: string, lang: LANG) {
    if (lang !== 'cn') {
        return name;
    }

    return restRequest<string>(`commons/enums/translate_name`, {type_id: typeId, name}, 'post');
}